import { gettext } from "../../shared/utils/legacy";
import { calcPct } from "../utils";
import useCachedUrlData from "../../shared/hooks/useCachedUrlData";
import WrapErrorLoading from "../../shared/components/WrapErrorLoading";
import cropr from "../../shared/utils/cropr";
import React, { useState, useRef } from "react";
import cx from "classnames";
import { Popover, Overlay } from "react-bootstrap";
import _ from "underscore";
import "./DashBoardSummaryTile.scss";
import DashboardSummaryInfo from "./DashboardSummaryInfo";

type sensorData = {
  id: number;
  name: string;
  product: number;
  soilMoistureStatus: string;
  status: string;
  period: string;
};
type sensorGroups = "normal" | "aboveFieldCapacity" | "belowRefillPoint" | "belowWarningPoint";
type sensorContent = {
  color: string;
  label: string;
  value: number;
  group: sensorGroups;
  filter: string;
  sensors: {
    id: number;
    name: string;
  }[];
};
const getSensorStatus = (sensors: sensorData[]) => {
  // Copied from sensorView.js and slightly modified
  let products: number[] = [];
  if (!sensors) return null;
  _.each(sensors, (sensor) => {
    if (!_.contains(products, sensor.product)) {
      products.push(sensor.product);
    }
  });
  const recentSensors = _.filter(sensors, (sensor) => {
      return sensor.period === "now";
    }),
    groupedSensors = _.groupBy(recentSensors, (sensor) => {
      return sensor.soilMoistureStatus as sensorGroups;
    });
  let result = {
    counter: {
      total: sensors.length,
      products: products.length,
      active: recentSensors.length,
      normal: undefined as undefined | number,
    },
    data: {
      sortOrder: "value-desc",
      content: [] as sensorContent[],
    },
  };

  (Object.entries(groupedSensors) as Array<[sensorGroups, sensorData[]]>).forEach(
    ([group, sensors]) => {
      let color = "#DFE5E7",
        filter = "105";
      switch (group) {
        case "normal":
          color = "#15C386";
          filter = "104";
          break;
        case "aboveFieldCapacity":
          color = "#1B75D0";
          filter = "103";
          break;
        case "belowRefillPoint":
          color = "#FF695B";
          filter = "101";
          break;
        case "belowWarningPoint":
          color = "#FFC341";
          filter = "102";
          break;
      }

      result["data"]["content"].push({
        color: color,
        label: sensors.length.toString(),
        value: sensors.length,
        group: group,
        filter: filter,
        sensors: sensors.map((sensor) => {
          return {
            id: sensor.id,
            name: sensor.name,
          };
        }),
      });
    }
  );
  const normalGroup = _.findWhere(result["data"]["content"], { group: "normal" });
  result["counter"]["normal"] = (normalGroup && normalGroup.value) || 0;
  return result;
};

const groupTranslations = {
  noData: gettext("No measurements"),
  normal: gettext("Normal moisture balance"),
  aboveFieldCapacity: gettext("Above field capacity"),
  belowRefillPoint: gettext("Below refill point"),
  belowWarningPoint: gettext("Below warning point"),
} as { [group in sensorGroups]: string };

interface SensorPartProperties {
  i: number;
  c: sensorContent;
  setPopover: (open: boolean) => void;
  showPopover: boolean;
  active: number;
  length: number;
}
const SensorPart = ({ c, setPopover, showPopover, i, active, length }: SensorPartProperties) => {
  const target = useRef(null);
  const valuePct = calcPct(c.value, active);
  return (
    <div
      ref={target}
      key={c.group}
      id={"Sensor" + i}
      role="progressbar"
      aria-valuenow={valuePct}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        backgroundColor: c.color,
        width: valuePct + "%",
      }}
      className={cx("cursor-pointer", "dashboard-summary-tile", "text-nowrap", "progress-bar")}
      onMouseEnter={() => setPopover(true)}
      onMouseLeave={() => setPopover(false)}
      /*TODO link to filtered view */
      onClick={() => window.open("/apps/dacom_online/sensor_overview/page/", "_self")}
    >
      {c.value}
      {showPopover && (
        <Overlay target={target.current} show={showPopover} placement="bottom">
          <Popover id={"Sensor" + i} placement="bottom" className={"summary_widget_popover"}>
            <Popover.Title>{groupTranslations[c.group]}</Popover.Title>
            <Popover.Content className="mh-80px overflow-auto">
              <ul className="list-group list-group-flush">
                {c.sensors
                  .sort((a, b) => (a.name < b.name ? -1 : 1))
                  .map(({ id, name }) => (
                    <li key={id} className="list-group-item py-1">
                      {/*TODO link to detail page for this sensor id*/}
                      <a
                        href={"/apps/dacom_online/sensor_overview/page/"}
                        data-metrics_category="summary_widget"
                        data-metrics_sub_category="sensor_overview"
                      >
                        {name}
                      </a>
                    </li>
                  ))}
              </ul>
            </Popover.Content>
          </Popover>
        </Overlay>
      )}
    </div>
  );
};

function SensorComponent() {
  const [popoverOpen, setPopoverOpen] = useState("");
  const { data, error } = useCachedUrlData<any>("/cropr/dashboard/sensor_widget_json/");
  const sensorStatus = getSensorStatus(data);
  const noSensors = sensorStatus && sensorStatus.counter.active === 0;
  // const requiresAttention = sensorStatus.counter.active - sensorStatus.counter.normal; // unclear if widget should contain this
  return (
    <WrapErrorLoading error={error}>
      {() =>
        (data && noSensors && <>{gettext("No active sensors")}</>) || (
          <div className={cx("dashboard-summary-tile", "progress")}>
            {sensorStatus &&
              sensorStatus.data.content
                .sort((a: sensorContent, b: sensorContent) => (a.value > b.value ? -1 : 1))
                .map((c: sensorContent, i: number) => (
                  <SensorPart
                    i={i}
                    c={c}
                    key={i}
                    setPopover={(open) => setPopoverOpen(open ? c.group : "")}
                    showPopover={popoverOpen === c.group}
                    active={sensorStatus.counter.active}
                    length={sensorStatus.data.content.length}
                  />
                ))}
          </div>
        )
      }
    </WrapErrorLoading>
  );
}
export default function DashboardSummarySensor() {
  // Kleuren & volgorde zoals in huidige legenda
  // Balkje zoals ziekte
  // - Geen sensoren? Irrigatiemanagement promoten
  // bij hover zie je legenda
  // TODO - Sensor batterij leeg? Notificatie inschieten. NIET in widget
  return (
    <div className="col-4 col-md py-3">
      <h6 className="text-muted mb-3">{gettext("Sensors")}</h6>
      {((cropr.features.irrigation || cropr.features.sensetion) && <SensorComponent />) || (
        <DashboardSummaryInfo
          href="https://cropx.nl/producten/irrigatiemanagement/"
          category="sensor"
        />
      )}
    </div>
  );
}
